import { useCallback, useEffect, useRef, useState } from 'react';
import { DateField } from '@mui/x-date-pickers/DateField';
import { FieldRef, FieldSection } from '@mui/x-date-pickers/models';
import { flowRight } from 'lodash';
import { HORIZONTAL_NAV_KEYS, VERTICAL_NAV_KEYS } from 'common/constants/general';
import withRequired from 'components/WithRequired/withRequired';
import withTooltip from 'components/WithTooltip/withTooltip';
import { gridShortDate, SHORT_DATE_FORMAT } from 'utilities';
import type { DateFieldChangeHandler, GridDateEditorProps } from './GridDateEditor.props';
import { dateFieldSx, ValueContainer, ValueViewer } from './GridDateEditor.styles';
import { parseCellDate, resolveDateFromEditValue } from './GridDateEditor.utils';

const GridDateEditor = ({
  cell,
  onChange,
  onKeyDown,
  value,
  doubleClicked,
  setDoubleClicked,
}: GridDateEditorProps) => {
  const [error, setError] = useState<boolean>(false);
  const [date, setDate] = useState<Date | null>(() => resolveDateFromEditValue(value, cell.value));
  const fieldRef = useRef<FieldRef<FieldSection>>(null);

  useEffect(() => {
    if (value !== undefined) {
      setDate(value === '' ? null : parseCellDate(value));
    }
  }, [value]);

  useEffect(() => {
    if (doubleClicked) {
      setDoubleClicked?.(false);
    }
  }, [doubleClicked, setDoubleClicked]);

  useEffect(() => {
    fieldRef.current?.focusField(0);
  }, [cell.key]);

  const handleChange = useCallback<DateFieldChangeHandler>(
    (newValue, context) => {
      const valid = !!newValue && context.validationError === null;
      setError(!valid);
      // Only feed a value back into the date field once it's actually complete and valid
      if (valid) {
        setDate(newValue);
        onChange(gridShortDate(newValue as Date));
      }
    },
    [onChange]
  );

  const handleContainerKeyDownCapture = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (VERTICAL_NAV_KEYS.has(event.key)) {
        // Move up and down in the table instead of increasing/decreasing the field value
        event.stopPropagation();
        onKeyDown?.(event);
      }
    },
    [onKeyDown]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      event.stopPropagation();

      if (HORIZONTAL_NAV_KEYS.has(event.key)) {
        // Should not treat these movements as commit and shift to the adjacent cell
        return;
      }
      onKeyDown?.(event);
    },
    [onKeyDown]
  );

  if (cell.hidden) {
    return null;
  }

  return (
    <ValueViewer>
      <ValueContainer onKeyDownCapture={handleContainerKeyDownCapture}>
        <DateField
          id={`${cell.key}-date_editor`}
          unstableFieldRef={fieldRef}
          value={date}
          format={SHORT_DATE_FORMAT}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          sx={dateFieldSx(error)}
          variant="standard"
        />
      </ValueContainer>
    </ValueViewer>
  );
};

const enhance = flowRight(withTooltip, withRequired);

export default enhance(GridDateEditor);
